import {FINGERS,SHAPES,LENGTHS} from './design-model.js';
import {translateText} from './i18n/translate.js';
import {inlineMaterialImages} from './material-media.js';

export const DESIGN_EXPORT_WIDTH=1620;
export const DESIGN_EXPORT_HEIGHT=2280;
const LANGUAGES=['en','fr','de','es','zh'];
const DEFAULT_NAME='我的专属甲片';
const DEFAULT_NAMES=new Set(LANGUAGES.map(language=>translateText(DEFAULT_NAME,language)));
const DISCLAIMER='屏幕效果为设计参考，实际颜色、尺寸与材料以订单确认内容为准';
const xml=value=>String(value??'').replace(/[&<>"']/g,char=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&apos;'}[char]));
const label=(items,id)=>items.find(item=>item.id===id)?.name||id;

function sizeSource(design,index){
  if(design.size!=='custom')return `${design.size} 码`;
  if(design.sizeMode==='per-finger')return design.fingerSizes?.[index]?`${design.fingerSizes[index]} 码`:'尺寸未设置';
  if(design.sizeMode==='tip-number')return design.tipNumbers?.[index]!=null?`甲片编号 ${design.tipNumbers[index]}`:'尺寸未设置';
  return design.widths?.[index]!=null?`${design.widths[index]} mm`:'尺寸未设置';
}

// Only the system default name is UI copy. Custom names, including Chinese
// names, are customer content and must remain verbatim rather than translated.
export function designExportLabels(design,{language='en',brand='MINI MURAL',designCode=''}={}){
  const locale=LANGUAGES.includes(language)?language:'en',t=text=>translateText(text,locale);
  const name=typeof design.name==='string'&&design.name.trim()?design.name:DEFAULT_NAME;
  const shape=label(SHAPES,design.shape)+(design.shape?.startsWith('ns-')?'':` ${design.shapeVariant}`);
  const size=design.size==='custom'?(design.sizeMode==='per-finger'?'定制尺码':design.sizeMode==='tip-number'?'甲片号定制':'毫米定制'):`${design.size} 码`;
  return {
    language:locale,brand:String(brand),name:DEFAULT_NAMES.has(name.trim())?t(DEFAULT_NAME):name,
    fingers:Array.from({length:10},(_,index)=>({hand:t(index<5?'左手':'右手'),finger:t(FINGERS[index%5]),size:t(sizeSource(design,index))})),
    specification:[t(shape),t(label(LENGTHS,design.length)),t(size)].join(' · '),
    layerCount:t(`${design.nails.reduce((sum,nail)=>sum+nail.layers.length,0)} 个图层 · 10 枚专属设计`),
    signature:designCode?String(designCode):t('CREATED BY YOU · MADE FOR YOU'),disclaimer:t(DISCLAIMER),
  };
}

// Wrapping is independent of browser font loading, including long names with no
// spaces. It changes only caption layout, never the embedded nail artwork.
function wrappedLines(value,maxUnits){
  const lines=[];let line='',units=0;
  for(const char of String(value)){
    const width=/[\u2e80-\uffef]/.test(char)?1:/[MW@#%]/.test(char)?.85:char===' '?.3:.57;
    if(char==='\n'||units+width>maxUnits){lines.push(line);line='';units=0;if(char==='\n')continue;}
    line+=char;units+=width;
  }
  if(line||!lines.length)lines.push(line);
  return lines;
}
function text(value,x,y,size,{color='#666666',maxWidth=1360,weight=400,lineHeight=size*1.4}={}){
  const lines=wrappedLines(value,maxWidth/size);
  return `<text x="${x}" y="${y}" text-anchor="middle" font-size="${size}" font-weight="${weight}" fill="${color}">${lines.map((line,index)=>`<tspan x="${x}" y="${y+index*lineHeight}">${xml(line)}</tspan>`).join('')}</text>`;
}
function sizedNailSvg(svg){
  if(typeof svg!=='string'||!/^<svg\s/.test(svg))throw new Error('Expected an embedded nail SVG');
  return svg.replace(/^<svg\b([^>]*)>/,(_,attributes)=>`<svg${attributes.replace(/\s(?:width|height)=(?:"[^"]*"|'[^']*')/g,'')} width="220" height="380">`);
}

export function buildDesignExportSvg({design,nailSvgs,language='en',brand='MINI MURAL',designCode=''}){
  if(!Array.isArray(nailSvgs)||nailSvgs.length!==10)throw new Error('Ten nail artworks are required for export');
  const labels=designExportLabels(design,{language,brand,designCode});
  const body=nailSvgs.map((svg,index)=>{
    const finger=labels.fingers[index];
    return `<g data-export-finger="${index}" transform="translate(${115+(index%5)*275},${index<5?370:1030})">${sizedNailSvg(svg)}${text(finger.hand,110,414,18,{maxWidth:245})}${text(finger.finger,110,443,21,{color:'#333333',maxWidth:245})}${text(finger.size,110,476,18,{maxWidth:245})}</g>`;
  }).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${DESIGN_EXPORT_WIDTH}" height="${DESIGN_EXPORT_HEIGHT}" lang="${labels.language}" font-family="Arial, Helvetica, 'Microsoft YaHei', sans-serif"><rect width="1620" height="2280" fill="#f5f5f5"/><rect x="65" y="70" width="1490" height="2140" rx="32" fill="#ffffff" stroke="#e5e5e5"/>${text(labels.brand,810,190,44,{color:'#1f1f1f',weight:600})}${text(labels.name,810,260,32,{color:'#333333'})}${body}${text(labels.specification,810,1715,24)}${text(labels.layerCount,810,1810,22)}${text(labels.signature,810,1955,25,{color:'#333333'})}${text(labels.disclaimer,810,2070,20,{lineHeight:30})}</svg>`;
}

export async function createDesignPng({design,nailElements,language='en',brand='MINI MURAL',designCode=''}){
  const nailSvgs=await Promise.all(Array.from(nailElements).map(inlineMaterialImages));
  const source=buildDesignExportSvg({design,nailSvgs,language,brand,designCode});
  const url=URL.createObjectURL(new Blob([source],{type:'image/svg+xml;charset=utf-8'}));
  try{
    const image=new Image();
    await new Promise((resolve,reject)=>{image.onload=resolve;image.onerror=reject;image.src=url;});
    const canvas=document.createElement('canvas');canvas.width=DESIGN_EXPORT_WIDTH;canvas.height=DESIGN_EXPORT_HEIGHT;
    const context=canvas.getContext('2d');if(!context)throw new Error('Canvas export unavailable');
    context.drawImage(image,0,0);
    const blob=await new Promise(resolve=>canvas.toBlob(resolve,'image/png'));
    if(!blob)throw new Error('PNG export failed');
    return blob;
  }finally{URL.revokeObjectURL(url);}
}
